import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import firebaseConfig, { VAPID_KEY, isFirebaseConfigured } from './firebaseConfig';
import apiService from './apiService';
import { API } from '../config/apiConfig';

class WebPushService {
  constructor() {
    this.messaging = null;
    this.swRegistration = null;
    this.foregroundCallback = null;
    this.actionCallback = null;
    this.enabled = false;
  }

  get supported() {
    return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Called once after login. Registers the SW, requests permission and sends
  // the FCM token to the backend so calls can reach this web app when closed.
  async init(authToken) {
    if (!this.supported || !isFirebaseConfigured()) return false;
    try {
      const app = initializeApp(firebaseConfig);
      this.messaging = getMessaging(app);
      this._registerOnMessage();
      this.swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });
      await navigator.serviceWorker.ready;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return false;

      const token = await getToken(this.messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: this.swRegistration,
      });
      if (token) {
        apiService.setToken(authToken);
        await apiService.client.post(API.fcmToken, { token });
        this.enabled = true;
      }
      return this.enabled;
    } catch (err) {
      console.warn('Web push init error:', err?.message || err);
      return false;
    }
  }

  // Foreground messages (app tab open).
  onForegroundMessage(callback) {
    this.foregroundCallback = callback;
    this._registerOnMessage();
  }

  _registerOnMessage() {
    if (!this.messaging || this._onMessageRegistered) return;
    this._onMessageRegistered = true;
    onMessage(this.messaging, (payload) => {
      if (this.foregroundCallback) this.foregroundCallback(payload.data || {});
    });
  }

  // Messages posted by the service worker (Answer/Decline clicked on a
  // notification while the app was closed or in another tab).
  onAction(callback) {
    this.actionCallback = callback;
    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data?.type === 'call-action' && this.actionCallback) {
        this.actionCallback(event.data.url);
      }
    });
  }
}

const webPushService = new WebPushService();
export default webPushService;